import { ClipboardEventHandler, FocusEvent, KeyboardEvent } from 'react';
import { EmailInputError } from '../types';
import InputField from '../InputField';
import validateEmail from '../validation/validateEmail';
import { emailInputErrorValues } from '../validation/utils';

interface IProps {
  emails: string[];
  onBlur: (email: string, emailError?: string) => void;
  onPaste: (values: string[]) => void;
  onFocus?: () => void;
  emailDomains?: string[];
  placeholder?: string;
  hasError?: boolean;
  disabled?: boolean;
}

const EmailInput = ({
  emails,
  onPaste,
  onBlur,
  onFocus,
  emailDomains,
  placeholder,
  hasError,
  disabled,
}: IProps) => {
  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    const inputValue = event.target.value.trim();

    if (!inputValue) {
      onBlur(inputValue);
      return;
    }

    // Check email format and domain
    const validationResult = validateEmail(inputValue, emailDomains);

    if (validationResult?.error) {
      onBlur('', validationResult.error);
      return;
    }

    // Check for duplicates
    const isDuplicate = emails.some((recipient) => recipient === inputValue);

    if (isDuplicate) {
      onBlur('', emailInputErrorValues[EmailInputError.Duplicate]);
      return;
    }

    onBlur(inputValue);
    event.target.value = '';
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;

    if (event.key === 'Enter') {
      target.blur();
    }
  };

  const handlePaste: ClipboardEventHandler<HTMLInputElement> = (event) => {
    event.preventDefault();
    const target = event.target as HTMLInputElement;
    const clipboardData = event.clipboardData.getData('text');

    // We expect to receive a coma/semicolon separated list of emails
    if (clipboardData.length > 0) {
      const values = clipboardData
        .trim()
        // try to split list by comma or semicolon
        .split(/[,;\s]+/)
        // Filter-out duplicates and invalid values
        .filter((value) => {
          // Check format and domain
          const validationResult = validateEmail(value, emailDomains);
          // Check for duplicates
          const isDuplicate = emails.includes(value);

          return !isDuplicate && !validationResult?.error;
        });

      if (values.length > 0) {
        onPaste(values);
      }

      target.blur();
    }
  };

  return (
    <InputField
      onFocus={onFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      hasError={hasError}
      placeholder={placeholder}
      disabled={disabled}
      // prevent safari from auto-filling
      name="search"
      autoComplete="off"
    />
  );
};

export default EmailInput;
